import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { connect, shallowEqual, useDispatch, useSelector } from 'react-redux';
import BaseForm from 'containers/Base/BaseForm';
import {
  getSecurityCompanyName,
  getSecurityId,
  getRegionId,
  getGeneralInfo,
} from 'reducers/users';
import { getTimezone } from 'reducers/utils';
import { transformPatrolDefObject } from 'utils/DataTransform/Patrols/Form/PatrolDefObject';
import { everyDaySchedule, startTime, endTime } from 'mocks/utils';
import { actions as patrolTemplateActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/dataGridTable';
import {
  validationSchema,
  initialState,
  initialValues,
  generalSection,
  detailedSection,
  mobilePatrolServices,
} from './options';
import staticText from 'static/text_display.json';
import { PatrolDefInfo } from 'utils/constants/informationText';
import { getRegion } from 'reducers/region';
import { siteManageVisitorKey } from 'utils/constants/visitor';
import { HelpLinks } from 'utils/constants/helpLinks';
import { sitesMapConfig } from 'utils/constants/sites.js';
import { serviceTypes } from 'utils/constants/serviceTypes';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
import { SITE_FORMIK_KEYS } from 'utils/constants/sites';
import { hasService } from 'utils/helper/product';
import {
  getSecurityCompanyServices,
  getSitesEnabled,
} from 'reducers/securityCompany';
import isEmpty from 'lodash/isEmpty';

import { toggleModal } from 'reducers/ui';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite';
import { translatedKeyValueText } from 'utils/methods/helpers';

import { nameSpace } from '../Modules/nameSpace';
import { getPatrolTemplateDetailTransformed } from '../Selectors/patrolTemplateSelectors';
import { actions as embeddedFormActions } from 'modules/embeddedForms/reducer';
import { siteSelectors } from 'containers/Pages/Locations/Sites';

export const showSiteListBasedOnDepartments = (
  sites,
  selectedDept,
  assignedDeptListForSites,
) => {
  // should show bizClients that belong to selected departments plus that belong to all departments
  if (isEmpty(selectedDept) && isEmpty(assignedDeptListForSites)) {
    // if the user has not selected any options then show all bizClients
    return sites;
  } else if (isEmpty(selectedDept)) {
    // if the user cleared department options, all bizClients should be visible
    return sites;
  } else {
    // if the user has selected departments, then filter bizClient options accordingly
    return sites.filter(site => {
      const selectedDeptIds = selectedDept?.map(m => m?.id);
      const assignedDeptList = site.assignedDepartmentList;
      if (assignedDeptList.length === 0) {
        return true; // doesn't have the department assigned and belongs to every department
      } else {
        return assignedDeptList.some(
          // all the biz clients which has the assigned selected department
          s => selectedDeptIds.includes(s.id),
        );
      }
    });
  }
};

export const departmentExistInSites = vals => {
  const selectedDeptIds = vals.departments?.map(m => m?.id);
  const assignedDeptList = vals.site.assignedDepartmentList || [];
  // if no department is selected or bizClient has no assigned department then show all options to select any bizClient
  if (assignedDeptList.length === 0 || selectedDeptIds.length === 0) {
    return true;
  }
  return assignedDeptList?.some(sid => selectedDeptIds.includes(sid));
};

const PatrolDefForm = ({
  editMode,
  details,
  createPatrolDef,
  updatePatrolDef,
  closeForm,
  securityCompanyData,
  regionId,
  timezone,
  countries,
  languages,
  services,
  userAssignedDepartments = [],
  persistentStateName,
}) => {
  const dispatch = useDispatch();
  const [multisitePatrol, setMultisitePatrol] = useState(false);
  const [isSurfaceImageMap, setIsSurfaceImageMap] = useState(false);
  const [scheduledRequired, setScheduleRequired] = useState(false);
  const [allDay, setAllDay] = useState(false);
  const [everyDay, setEveryDay] = useState(false);
  const [returnToStart, setReturnToStart] = useState(true);
  const [selectedSiteLocation, setSiteLocation] = useState([]);
  const [toggleManageVisitor, setManageVisitor] = useState();
  const [path, setPath] = useState();
  const isMobilePatrolOnly = !services.includes(serviceTypes.patrol.singleSite);
  const [checkpoints, setCheckpoints] = useState([]);
  const [isVirtualPatrolEnabled, setIsVirtualPatrolEnabled] = useState();
  //this is the filtering for the site/department list
  const [siteAssignedDepartmentList, setSiteAssignedDepartmentList] = useState(
    [],
  );
  const [siteId, setSiteId] = useState('');
  const siteList = useSites();

  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSiteDetailImages, setSelectedSiteDetailImages] = useState();
  const [siteSurfaces, setSiteSurfaces] = useState([]);
  const [selectedSite, setSelectedSite] = useState();

  const siteEmbeddedDetails = useSelector(
    state => siteSelectors.siteEmbeddedDetails(state),
    shallowEqual,
  );
  const sitesEnable = useSelector(state => getSitesEnabled(state));
  const isPopupRequired = () => {
    if (checkpoints && checkpoints.length > 0 && siteId !== '') {
      return true;
    } else if (!isSurfaceImageMap && checkpoints && checkpoints.length === 0) {
      return true;
    } else return false;
  };

  const filteredDepartments =
    !isEmpty(siteAssignedDepartmentList) && !isEmpty(siteAssignedDepartmentList)
      ? userAssignedDepartments.filter(f =>
          siteAssignedDepartmentList.includes(f.id),
        )
      : userAssignedDepartments;

  const transformedInitVals = editMode
    ? initialValues(details)
    : initialState(timezone, isMobilePatrolOnly);

  const generalFormSection = generalSection({
    updatedValues: {
      ...(!isEmpty(siteEmbeddedDetails) && { site: siteEmbeddedDetails }),
    },
    popUpRequired: isPopupRequired(),
    multisitePatrol,
    userAssignedDepartments: filteredDepartments,
    countries,
    languages,
    toggleManageVisitor,
    isMobilePatrolOnly,
    isSurfaceImageMap,
    hasVisitorManagement: hasService({
      services,
      service: serviceTypes.visitorManagement,
    }),
    haveAllRequiredServices: mobilePatrolServices.every(item =>
      services.includes(item),
    ),
    sitesEnable,
    securityCompanyId: securityCompanyData.securityCompanyId,
    selectedDepartments,
  });

  const handlePatrolSelectEveryday = patrol => {
    if (patrol.everyDay) {
      patrol.schedule = everyDaySchedule;
    }
    setEveryDay(patrol.everyDay);
  };

  const handleReturnToStart = patrol => {
    setReturnToStart(patrol.returnToStart);
  };

  const handlePatrolSetAllDay = patrol => {
    if (patrol.allDay) {
      setAllDay(true);
      patrol.startTime = startTime;
      patrol.endTime = endTime;
    } else if (allDay && !patrol.allDay) {
      setAllDay(false);
    }
  };

  const monitorFormChanges = vals => {
    if (vals?.checkpoints) {
      setCheckpoints(vals?.checkpoints);
    }
    if (vals.virtualPatrol) {
      setIsVirtualPatrolEnabled(vals.virtualPatrol);
    } else {
      setIsVirtualPatrolEnabled(false);
    }
    if (vals?.departments) {
      setSelectedDepartments(vals?.departments);
      // check if the selected department exists in the selected sites
      // if yes, then don't change anything
      // if no, then remove the selected option from the site select field
      if (!isEmpty(vals.site) && !departmentExistInSites(vals)) {
        vals.site = '';
        // default siteSurfaceImages to geographical map
        vals.siteSurfaceImages = GEOGRAPHICAL_MAP;
        // clear the checkpoints
        vals.checkpoints = [];
      }
    }

    if (vals.mobilePatrol) {
      setMultisitePatrol(true);
    } else {
      setMultisitePatrol(false);
    }
    if (vals.schedule) {
      setScheduleRequired(true);
    } else {
      setScheduleRequired(false);
    }
    // set selectedSiteSurfaceImage
    if (vals?.siteSurfaceImages) {
      setSelectedSiteDetailImages(vals.siteSurfaceImages);
    }

    // set total site surfaces
    if (vals.site?.id) {
      setSiteId(vals.site?.id);
      const selectedSite = siteList.find(site => site.id === vals.site.id);
      setSelectedSite(selectedSite);
      setSiteSurfaces(vals.site.surfaces);
      setSiteAssignedDepartmentList(vals.site?.assignedDepartmentList);
      setSiteLocation(get(vals.site, 'locationDetail', []));
    } else {
      setSelectedSite(); // clears the selected site polygon
      setSiteLocation(); // clears dark highlight
    }

    if (vals.siteLocations && vals.siteLocations.length > 0) {
      const sitePaths = vals.siteLocations[0].paths;
      if (sitePaths && sitePaths.length > 2) {
        setPath(sitePaths);
      }
    }
    if (
      vals.siteSurfaceImages &&
      vals.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id
    ) {
      setIsSurfaceImageMap(true);
    } else {
      setIsSurfaceImageMap(false);
    }
    setManageVisitor(!vals[siteManageVisitorKey]);
    handlePatrolSelectEveryday(vals);
    handlePatrolSetAllDay(vals);
    handleReturnToStart(vals);
  };

  const transformPatrolObject = values => {
    const site = siteList.find(site => site.id === values?.site?.id);
    // check if the siteSurfaceImages.id is not Geographical Map
    // change the checkpoints according to the image coordinates between 0-1
    // eslint-disable-next-line prefer-const
    let vals = { ...values };
    if (
      vals.checkpoints &&
      vals.siteSurfaceImages &&
      vals.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id
    ) {
      Reflect.deleteProperty(vals, SITE_FORMIK_KEYS.siteSurfaceImages);
      Reflect.deleteProperty(vals, SITE_FORMIK_KEYS.surfaceId);
    } else {
      vals['checkpoints'] = vals.checkpoints.map(m => {
        return {
          ...m,
          lat: parseFloat((1 - m.lng / vals.imageDimensions.height).toFixed(2)),
          lng: parseFloat((m.lat / vals.imageDimensions.width).toFixed(2)),
        };
      });
    }
    return transformPatrolDefObject({
      ...vals,
      site,
      regionId,
      ...securityCompanyData,
      returnToStart,
      proximityAlertDisabled: values.virtualPatrol,
    });
  };

  const onCreate = values => {
    createPatrolDef(transformPatrolObject(values));
  };

  const onEdit = values => {
    const patrolDefId = get(details, 'others.id.value');

    updatePatrolDef(patrolDefId, transformPatrolObject(values));
  };

  useEffect(() => {
    return () => {
      // clear embedded details
      dispatch(embeddedFormActions.clearEmbeddedFormDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BaseForm
      imageRequired={false}
      edit={editMode}
      initialValues={{
        ...(!isEmpty(siteEmbeddedDetails) && { site: siteEmbeddedDetails }),
        ...transformedInitVals,
      }}
      formTitle={
        editMode
          ? staticText.managerial_view.patrol.edit_default
          : staticText.managerial_view.patrol.create
      }
      saveHoverText={
        editMode
          ? translatedKeyValueText('update_value_title', {
              value: details.generalInfo.patrolName.value,
            })
          : staticText.managerial_view.patrol.create
      }
      closeForm={closeForm}
      onChange={monitorFormChanges}
      onSubmit={editMode ? onEdit : onCreate}
      validationSchema={validationSchema({
        multisitePatrol,
        scheduledRequired,
      })}
      generalSection={generalFormSection}
      detailedSection={detailedSection({
        isVirtualPatrolEnabled,
        siteSurfaces,
        multisitePatrol,
        selectedSiteDetailImages,
        scheduledRequired,
        allDay,
        selectedSite,
        siteLocation: path
          ? [
              {
                ...sitesMapConfig,
                paths: path.map(point => ({
                  lat: point.lat,
                  lng: point.lng,
                })),
              },
            ]
          : selectedSiteLocation,
        everyDay,
      })}
      hoverText={editMode ? details.generalInfo.patrolName.value : ''}
      note={PatrolDefInfo.note}
      formInfo={{
        link: HelpLinks.patrolLink,
        value: HelpLinks.patrolText,
      }}
      // confirmPopup={confirmPopup}
      persistentStateName={persistentStateName}
    />
  );
};

PatrolDefForm.propTypes = {
  regionId: PropTypes.string,
  details: PropTypes.object,
  editMode: PropTypes.bool,
  securityCompanyData: PropTypes.object,
  createPatrolDef: PropTypes.func,
  updatePatrolDef: PropTypes.func,
  siteList: PropTypes.array,
  userAssignedDepartments: PropTypes.array,
  closeForm: PropTypes.func,
  toggleForm: PropTypes.func,
  timezone: PropTypes.string,
  persistentStateName: PropTypes.string,
  generalInfo: PropTypes.object,
  countries: PropTypes.array,
  languages: PropTypes.array,
  regionName: PropTypes.string,
  services: PropTypes.array,
  selectedSiteDetailImages: PropTypes.func,
  togglePatrolForm: PropTypes.func,
};

const mapStateToProps = state => ({
  details: getPatrolTemplateDetailTransformed(state),
  securityCompanyData: {
    securityCompanyId: getSecurityId(state),
    securityCompanyName: getSecurityCompanyName(state),
  },
  regionId: getRegionId(state),
  regionName: getRegion(state).name,
  timezone: getTimezone(state),
  countries: state.utils.countries,
  languages: state.utils.languages,
  generalInfo: getGeneralInfo(state),
  services: getSecurityCompanyServices(state),
});

const mapDispatchToProps = dispatch => ({
  createPatrolDef: patrolDef =>
    dispatch(patrolTemplateActions.createRowEntry({ patrolDef })),
  updatePatrolDef: (id, patrolDef) =>
    dispatch(patrolTemplateActions.updateRowEntry({ id, patrolDef })),
  togglePatrolForm: () => dispatch(toggleModal(nameSpace)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatrolDefForm);
